import React, { useState } from "react";
import { CurrencyDollarIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

const getIconForItem = (kind, itemName) => {
  const iconClasses = "w-6 h-6 text-indigo-600";
  const normalizedItem = String(itemName || "").toLowerCase();

  switch (true) {
    case kind === "milk" || normalizedItem.includes("milk"):
      return (
        <img src="/icons/milk-can.png" alt="" className="w-6 h-6" />
      );
    case kind === "money" || normalizedItem.includes("money"):
      return <CurrencyDollarIcon className={iconClasses} />;
    case kind === "ghee" || normalizedItem.includes("ghee"):
      return <img src="/icons/ghee.png" alt="" className="w-6 h-6" />;
    case kind === "feed" || normalizedItem.includes("feed"):
      return <img src="/icons/feed.png" alt="" className="w-6 h-6" />;
    default:
      return <CurrencyDollarIcon className={iconClasses} />;
  }
};

// Color logic now returns a class based on the transaction type
const getColorForTransaction = (kind, itemName, amount) => {
  const numericAmount = Number(amount);
  if (Number.isFinite(numericAmount) && numericAmount !== 0) {
    return numericAmount > 0 ? "text-green-600" : "text-red-500";
  }

  if (["feed", "ghee"].includes(kind) || /Money Given|Feed|Ghee/i.test(itemName)) {
    return "text-red-500";
  }
  if (kind === "milk" || /Money Received|Milk/i.test(itemName)) {
    return "text-green-600";
  }
  return "text-gray-500";
};

const TransactionItem = ({ data, onDelete,role }) => {
  const { t, i18n } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(null);
  const handleViewDetails = (item) => setSelectedItem(item);
  const closeDetails = () => setSelectedItem(null);
  const formatEntryDate = (value) => {
    if (!value) return t("balanceSheet.details.not_available");
    const normalizedDate = String(value).slice(0, 10);
    const parsedDate = new Date(`${normalizedDate}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return normalizedDate;

    return parsedDate.toLocaleDateString(
      i18n.resolvedLanguage?.startsWith("hi") ? "hi-IN" : "en-IN",
      { day: "2-digit", month: "short", year: "numeric" },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {data.map((item, index) => {
        const itemColorClass = getColorForTransaction(
          item.kind,
          item.item,
          item.money,
        );
        const amount = Number(item.money || 0);
        const amountSign = amount > 0 ? "+" : amount < 0 ? "−" : "";

        return (
          <div
            key={index}
            className={`flex items-center justify-between p-4 bg-white drop-shadow-sm rounded-lg transition-all duration-200 hover:drop-shadow-md hover:scale-[1.01] transform ${
              item.kind !== "milk" ? "cursor-pointer" : ""
            }`}
            onClick={
              item.kind !== "milk"
                ? () => handleViewDetails(item)
                : undefined
            }
          >
            {/* Left Side: Icon and Text */}
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 p-2 bg-gray-50 rounded-full transition-colors duration-200">
                {getIconForItem(item.kind, item.item)}
              </div>
              <div className="flex flex-col">
                <p className="font-semibold text-base sm:text-lg text-gray-900">
                  {item.item}
                </p>
                <div className="text-xs sm:text-sm text-gray-500 mt-1 flex flex-wrap gap-x-3">
                  {item.date && <span>{formatEntryDate(item.date)}</span>}
                  {item.quantityDisplay && <span>{item.quantityDisplay}</span>}
                  {item.name && <span>{item.name}</span>}
                </div>
              </div>
            </div>

            {/* Right Side: Amount */}
            <p
              className={`whitespace-nowrap font-bold text-lg sm:text-xl ${itemColorClass}`}
            >
              {amountSign}{Math.abs(amount).toFixed(2)} ₹
            </p>
          </div>
        );
      })}

      {/* Minimal Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-20 flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              {t("balanceSheet.details.title")}
            </h3>
            <ul className="text-base text-gray-700 space-y-3">
              <li className="flex justify-between items-center">
                <span className="font-medium">{t("balanceSheet.details.item")}:</span>
                <span>{selectedItem.item}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-medium">{t("balanceSheet.details.date")}:</span>
                <span>{formatEntryDate(selectedItem.date)}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-medium">{t("balanceSheet.details.quantity")}:</span>
                <span>{selectedItem.quantityDisplay || t("balanceSheet.details.not_available")}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-medium">{t("balanceSheet.details.name")}:</span>
                <span>{selectedItem.name || t("balanceSheet.details.not_available")}</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="font-medium">{t("balanceSheet.details.amount")}:</span>
                <span
                  className={`font-bold ${getColorForTransaction(
                    selectedItem.kind,
                    selectedItem.item,
                    selectedItem.money,
                  )}`}
                >
                  {Number(selectedItem.money) > 0
                    ? "+"
                    : Number(selectedItem.money) < 0
                    ? "−"
                    : ""}
                  {Math.abs(Number(selectedItem.money || 0)).toFixed(2)} ₹
                </span>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              {role !== "associated user" && (
              <button
                onClick={() => {
                  onDelete(selectedItem.id, "borrow");
                  closeDetails();
                }}
                className="flex-1 bg-red-600 text-white font-medium py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                {t("balanceSheet.actions.delete")}
              </button>)}
              <button
                onClick={closeDetails}
                className="flex-1 text-gray-700 border border-gray-300 font-medium py-2 rounded-md hover:bg-gray-100 transition-colors"
              >
                {t("balanceSheet.actions.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionItem;
